import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Heart, Search, Users, CheckSquare, Wallet, Star, ArrowRight, MapPin, Store, Calendar, Mail, Globe, Building2, Image, Palette, Music, Sparkles, Tag, BookOpen, ChevronLeft, ChevronRight } from '../icons';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination, EffectFade, Navigation } from 'swiper/modules';
import Lottie from 'lottie-react';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';
import 'swiper/css/navigation';
import VendorCard from '../components/VendorCard';
import LaganCalendar from '../components/LaganCalendar';
import { getVendors, getCategories, getAppSettings, getHeroImages, getVendorCities } from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import useScrollReveal from '../hooks/useScrollReveal';
import { categoryImage } from '../utils/categoryImages';
import mediaUrl from '../utils/mediaUrl';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/animations/scale.css';

const categoryTileColors = {
  'venues':            '#e74c3c',
  'photographers':     '#9b59b6',
  'caterers':          '#3498db',
  'decorators':        '#e91e63',
  'mehendi-artists':   '#ff5722',
  'djs-music':         '#e67e22',
  'beauty-makeup':     '#00bcd4',
  'florists':          '#2ecc71',
  'pandits-priests':   '#8bc34a',
  'wedding-planners':  '#f39c12',
  'invitation-cards':  '#607d8b',
  'jewellers':         '#ffc107',
  'bridal-wear':       '#e91e63',
  'panche-baja':       '#D35400',
};

const categoryDescriptions = {
  'venues':           'Banquet halls, party palaces & outdoor venues across Nepal',
  'photographers':    'Capture every precious moment with skilled wedding photographers',
  'caterers':         'Delicious Nepali & fusion cuisine for your wedding feast',
  'decorators':       'Stunning floral & themed décor to transform your venue',
  'mehendi-artists':  'Beautiful traditional mehendi & henna designs for brides',
  'djs-music':        'DJs, live bands & modern sound systems for wedding receptions',
  'panche-baja':      'Traditional Nepali Panche Baja ensembles for auspicious wedding ceremonies',
  'beauty-makeup':    'Bridal makeup, hair styling & beauty packages for your big day',
  'florists':         'Fresh floral arrangements, bouquets & stage decorations',
  'pandits-priests':  'Experienced pandits for all Vedic wedding rituals & ceremonies',
  'wedding-planners': 'Expert planners to coordinate every detail of your wedding',
  'invitation-cards': 'Elegant printed & digital invitation cards & stationery',
  'jewellers':        'Bridal jewellery, gold sets & custom ornaments for every look',
  'bridal-wear':      'Bridal lehengas, sarees, sherwanis & traditional wedding attire',
};

const categoryIcons = {
  venues: Building2, photographers: Image, caterers: Store, decorators: Palette,
  'mehendi-artists': Sparkles, 'djs-music': Music, 'beauty-makeup': Sparkles,
  florists: Heart, 'pandits-priests': BookOpen, 'wedding-planners': CheckSquare,
  'invitation-cards': Mail, jewellers: Tag, 'bridal-wear': Star, 'panche-baja': Music,
};

const TESTIMONIALS = [
  {
    img:  'https://images.pexels.com/photos/31820500/pexels-photo-31820500.jpeg?auto=compress&cs=tinysrgb&w=300&h=300&fit=crop',
    name: 'Aarav & Priya',
    role: 'Kathmandu',
    text: 'NepWed made our traditional Kathmandu wedding absolutely seamless. Finding vendors was so easy, and the checklist kept us on track throughout the planning!',
  },
  {
    img:  'https://images.pexels.com/photos/12543680/pexels-photo-12543680.jpeg?auto=compress&cs=tinysrgb&w=300&h=300&fit=crop',
    name: 'Bikash & Sita',
    role: 'Pokhara',
    text: 'Our fusion wedding was beautifully coordinated thanks to the vendors we found on NepWed. The budget tracker saved us lakhs!',
  },
  {
    img:  'https://images.pexels.com/photos/31820568/pexels-photo-31820568.jpeg?auto=compress&cs=tinysrgb&w=300&h=300&fit=crop',
    name: 'Rohit & Anita',
    role: 'Lalitpur',
    text: 'From booking the venue to managing 200 guests, NepWed\'s tools made everything easy. Highly recommend for any Nepali couple!',
  },
];

const LAGAN_2083 = [
  { month: 0, day: 7,  adDate: 'Mon, Apr 20, 2026' }, { month: 0, day: 8,  adDate: 'Tue, Apr 21, 2026' },
  { month: 0, day: 22, adDate: 'Tue, May 5, 2026'  }, { month: 0, day: 23, adDate: 'Wed, May 6, 2026'  },
  { month: 0, day: 24, adDate: 'Thu, May 7, 2026'  }, { month: 0, day: 25, adDate: 'Fri, May 8, 2026'  },
  { month: 0, day: 30, adDate: 'Wed, May 13, 2026' }, { month: 0, day: 31, adDate: 'Thu, May 14, 2026' },
  { month: 2, day: 9,  adDate: 'Wed, Jun 24, 2026' }, { month: 2, day: 13, adDate: 'Sun, Jun 28, 2026' },
  { month: 2, day: 14, adDate: 'Mon, Jun 29, 2026' }, { month: 2, day: 15, adDate: 'Tue, Jun 30, 2026' },
  { month: 2, day: 17, adDate: 'Thu, Jul 2, 2026'  }, { month: 2, day: 18, adDate: 'Fri, Jul 3, 2026'  },
  { month: 2, day: 23, adDate: 'Wed, Jul 8, 2026'  },
  { month: 7, day: 9,  adDate: 'Wed, Nov 25, 2026' }, { month: 7, day: 10, adDate: 'Thu, Nov 26, 2026' },
  { month: 7, day: 16, adDate: 'Wed, Dec 2, 2026'  }, { month: 7, day: 17, adDate: 'Thu, Dec 3, 2026'  },
  { month: 7, day: 19, adDate: 'Sat, Dec 5, 2026'  }, { month: 7, day: 24, adDate: 'Thu, Dec 10, 2026' },
  { month: 7, day: 25, adDate: 'Fri, Dec 11, 2026' }, { month: 7, day: 26, adDate: 'Sat, Dec 12, 2026' },
  { month: 9, day: 4,  adDate: 'Mon, Jan 18, 2027' }, { month: 9, day: 12, adDate: 'Tue, Jan 26, 2027' },
  { month: 9, day: 15, adDate: 'Fri, Jan 29, 2027' }, { month: 9, day: 19, adDate: 'Tue, Feb 2, 2027'  },
  { month: 9, day: 20, adDate: 'Wed, Feb 3, 2027'  }, { month: 9, day: 27, adDate: 'Wed, Feb 10, 2027' },
  { month: 10, day: 3,  adDate: 'Mon, Feb 15, 2027' }, { month: 10, day: 4,  adDate: 'Tue, Feb 16, 2027' },
  { month: 10, day: 11, adDate: 'Tue, Feb 23, 2027' }, { month: 10, day: 12, adDate: 'Wed, Feb 24, 2027' },
  { month: 10, day: 15, adDate: 'Sat, Feb 27, 2027' }, { month: 10, day: 16, adDate: 'Sun, Feb 28, 2027' },
  { month: 10, day: 17, adDate: 'Mon, Mar 1, 2027'  }, { month: 10, day: 18, adDate: 'Tue, Mar 2, 2027'  },
  { month: 10, day: 20, adDate: 'Thu, Mar 4, 2027'  }, { month: 10, day: 25, adDate: 'Tue, Mar 9, 2027'  },
  { month: 10, day: 26, adDate: 'Wed, Mar 10, 2027' }, { month: 10, day: 30, adDate: 'Sun, Mar 14, 2027' },
];
const BS_MONTHS_EN = ['Baisakh','Jestha','Asar','Shrawan','Bhadra','Ashoj','Kartik','Mangsir','Poush','Magh','Falgun','Chaitra'];

// Bikram Sambat conversion (covers 2081–2084 BS)
const BS_CAL = {
  2081: { start: new Date(2024, 3, 13), m: [31,31,32,32,31,30,30,29,30,29,30,31] },
  2082: { start: new Date(2025, 3, 14), m: [31,31,32,32,31,30,30,29,30,29,30,30] },
  2083: { start: new Date(2026, 3, 14), m: [31,31,32,31,31,30,30,29,30,29,30,30] },
  2084: { start: new Date(2027, 3, 14), m: [31,32,31,32,31,30,30,29,30,29,30,30] },
};
function adToBS(ad) {
  const d = new Date(ad.getFullYear(), ad.getMonth(), ad.getDate());
  const years = Object.keys(BS_CAL).map(Number).sort((a, b) => b - a);
  let yr = years[years.length - 1];
  for (const y of years) { if (d >= BS_CAL[y].start) { yr = y; break; } }
  let diff = Math.floor((d - BS_CAL[yr].start) / 86400000);
  let mo = 0;
  for (let i = 0; i < 12; i++) {
    if (diff < BS_CAL[yr].m[i]) { mo = i; break; }
    diff -= BS_CAL[yr].m[i];
  }
  return { year: yr, month: BS_MONTHS_EN[mo], day: diff + 1 };
}

function Home() {
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  const { user } = useAuth();

  useEffect(() => {
    if (user?.role === 'vendor_employee') navigate('/my-projects');
  }, [user, navigate]);

  const [featuredVendors, setFeaturedVendors] = useState([]);
  const [loadingVendors, setLoadingVendors]   = useState(true);
  const [categories, setCategories]           = useState([]);
  const [searchQuery, setSearchQuery]         = useState('');
  const [cityQuery, setCityQuery]             = useState('');
  const [categoryQuery, setCategoryQuery]     = useState('');
  const [showAnimation, setShowAnimation]     = useState(true);
  const [showStats, setShowStats]               = useState(true);
  const [weddingAnimationData, setWeddingAnimationData] = useState(null);
  
  // Ref for lagan strip scrolling
  const laganScrollRef = useRef(null);

  // Until the API answers, offer the main cities; afterwards only cities that
  // actually have approved vendors, so no choice leads to an empty listing.
  const [cities, setCities] = useState(['Kathmandu', 'Lalitpur', 'Bhaktapur', 'Pokhara', 'Chitwan', 'Biratnagar', 'Butwal', 'Birgunj', 'Dharan']);

  const heroImg = (id) => `https://images.pexels.com/photos/${id}/pexels-photo-${id}.jpeg?auto=compress&cs=tinysrgb&w=1600&h=900&fit=crop`;
  // Slide text is looked up at render time; storing translated strings in state
  // kept the hero in the old language after the language toggle.
  const heroCopy = [
    { title: t('home.heroTitle1'), highlight: t('home.heroHighlight1'), sub: t('home.heroSubtitle1') },
    { title: t('home.heroTitle2'), highlight: t('home.heroHighlight2'), sub: t('home.heroSubtitle2') },
    { title: t('home.heroTitle3'), highlight: t('home.heroHighlight3'), sub: t('home.heroSubtitle3') },
  ];
  const [heroSlides, setHeroSlides] = useState([
    { img: heroImg(38259819) }, { img: heroImg(11652319) }, { img: heroImg(29137533) },
  ]);

  useEffect(() => {
    // Vendors come from our own database only. Google-sourced vendors are already
    // upserted into it by the import, so the homepage does not need a live Places
    // call — one used to fire 13 categories x 3 cities of billed search on every
    // single page load.
    Promise.all([
      getVendors({ featured: 'true', limit: 10 }).catch(() => null),
      getVendors({ limit: 12, sort_by: 'rating' }).catch(() => null),
    ])
      .then(([featuredRes, topRes]) => {
        const featured = featuredRes?.data?.vendors || [];
        const top = topRes?.data?.vendors || [];
        // Top up the carousel with well-rated vendors when few are flagged featured.
        const seen = new Set(featured.map((v) => v.id));
        const filled = [...featured, ...top.filter((v) => !seen.has(v.id))].slice(0, 10);
        setFeaturedVendors(filled);
      })
      .finally(() => setLoadingVendors(false));
    getCategories().then((res) => setCategories(res.data)).catch(() => {});
    getVendorCities()
      .then((res) => { if (Array.isArray(res.data) && res.data.length) setCities(res.data); })
      .catch(() => {});
    getAppSettings()
      .then((res) => {
        setShowAnimation(res.data.wedding_animation_enabled !== 'false');
        setShowStats(res.data.stats_section_enabled !== 'false');
      })
      .catch(() => {});
    fetch('https://lottie.host/47e122ee-c5e8-4b84-8ee2-87ad449a498d/NZywxqWQpt.json')
      .then(r => r.json()).then(data => setWeddingAnimationData(data)).catch(() => {});
    getHeroImages()
      .then((res) => {
        if (res.data && res.data.length > 0) {
          setHeroSlides(res.data.slice(0, 3).map((img) => ({
            img: mediaUrl(img.image_url),
            media_type: img.media_type || 'image',
            video_url: img.video_url || '',
          })));
        }
      })
      .catch(() => {});
  }, []);

  const getYouTubeId = (url) => {
    try {
      const u = new URL(url);
      if (u.hostname.includes('youtu.be')) return u.pathname.slice(1);
      return u.searchParams.get('v') || '';
    } catch { return ''; }
  };

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (searchQuery) params.set('search', searchQuery);
    if (cityQuery) params.set('city', cityQuery);
    const qs = params.toString();
    // No category chosen means every category, not silently Wedding Planners.
    const base = categoryQuery ? `/vendors/${categoryQuery}` : '/vendors';
    navigate(`${base}${qs ? '?' + qs : ''}`);
  };

  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Nepal = UTC+5:45
  const nepalTime = new Date(now.getTime() + (5 * 60 + 45 + now.getTimezoneOffset()) * 60000);
  const localHour = now.getHours();
  const greetWord = localHour < 12 ? t('home.greetMorning') : localHour < 17 ? t('home.greetAfternoon') : localHour < 21 ? t('home.greetEvening') : t('home.greetNight');
  const greetEmoji = localHour < 12 ? '🌅' : localHour < 17 ? '☀️' : localHour < 21 ? '🌆' : '🌙';
  const localDateStr = now.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  const localTimeStr = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const nepalTimeStr = nepalTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const bsDate = adToBS(nepalTime);
  const nepalBSStr = `${bsDate.month} ${bsDate.day}, ${bsDate.year} BS`;

  useScrollReveal([categories.length, featuredVendors.length, showStats, user]);

  // The API lists categories alphabetically, which left Venues and Photographers
  // out of the ten hero tiles entirely. Lead with venues, then the busiest.
  const sortedCategories = React.useMemo(() => {
    const rank = (c) => (c.slug === 'venues' ? Infinity : parseInt(c.vendor_count || 0, 10));
    return [...categories].sort((a, b) => rank(b) - rank(a));
  }, [categories]);
  const gridCategories = sortedCategories.filter((cat) => cat.slug !== 'invitation-cards');
  // The first card is a 2x2 feature. Enough trailing cards go 2-wide that the
  // 4-column grid ends on a full row; the 2-column layouts then do too.
  const wideCategoryCount = (4 - ((gridCategories.length + 3) % 4)) % 4;

  const upcomingLagan = React.useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const parseAD = s => { const d = new Date(s.replace(/^[A-Za-z]+,\s*/, '')); d.setHours(0,0,0,0); return d; };
    return LAGAN_2083
      .map(l => ({ ...l, adDateObj: parseAD(l.adDate) }))
      .filter(l => l.adDateObj >= today)
      .slice(0, 6);
  }, []);

  // Scroll handler for lagan strip
  const scrollLaganStrip = (direction) => {
    if (laganScrollRef.current) {
      const scrollAmount = 200;
      laganScrollRef.current.scrollBy({ 
        left: direction === 'left' ? -scrollAmount : scrollAmount, 
        behavior: 'smooth' 
      });
    }
  };

  return (
    <div className="home-page">

      {/* ── GREETING BAR ── */}
      <div className="home-greeting-bar">
        <span className="home-greeting-text">{greetEmoji} {greetWord}{user ? `, ${user.first_name}` : ''}</span>
        <span className="home-greeting-sep greeting-detail"> · </span>
        <span className="greeting-detail"><span className="home-greeting-date">{localDateStr}</span> <span className="home-greeting-time">{localTimeStr}</span></span>
        <span className="home-greeting-sep greeting-detail"> · </span>
        <span className="greeting-detail"><span className="home-greeting-date">{nepalBSStr}</span> <span className="home-greeting-time">{nepalTimeStr} NPT</span></span>
      </div>

      {/* ── 1. HERO ── */}
      <section className="hero swiper-hero-section">
        {/* Anchors the pagination to the slides, so on phones — where the
            category tiles drop below the hero instead of overlaying it — the
            dots stay on the photo rather than landing on the tiles. */}
        <div className="hero-swiper-wrap">
          <Swiper
            modules={[Autoplay, Pagination, EffectFade, Navigation]}
            effect="fade"
            speed={1200}
            autoplay={{ delay: 5500, disableOnInteraction: false }}
            pagination={{ clickable: true, el: '.hero-swiper-pagination' }}
            loop
            className="hero-swiper"
          >
            {heroSlides.map((slide, i) => (
              <SwiperSlide key={i}>
                <div className="hero-slide">
                  {slide.media_type === 'video' && slide.video_url ? (
                    slide.video_url.includes('youtube.com') || slide.video_url.includes('youtu.be') ? (
                      <div className="hero-slide-video-wrap">
                        <iframe
                          src={`https://www.youtube.com/embed/${getYouTubeId(slide.video_url)}?autoplay=1&mute=1&loop=1&playlist=${getYouTubeId(slide.video_url)}&controls=0&showinfo=0&rel=0&modestbranding=1`}
                          title="Hero video" frameBorder="0"
                          allow="autoplay; encrypted-media" allowFullScreen
                          className="hero-slide-iframe"
                        />
                      </div>
                    ) : (
                      <video
                        className="hero-slide-video"
                        src={mediaUrl(slide.video_url)}
                        autoPlay muted loop playsInline
                      />
                    )
                  ) : (
                    <div className="hero-slide-bg" style={{ backgroundImage: `url(${slide.img})` }} />
                  )}
                  <div className="hero-slide-overlay" />

                  <div className="hero-centered-content">
                    {showAnimation && weddingAnimationData && i === 0 && (
                      <div className="hero-lottie-top">
                        <Lottie animationData={weddingAnimationData} loop autoplay style={{ width: 72, height: 72 }} />
                      </div>
                    )}
                    <span className="hero-trust-badge">
                      <span className="hero-trust-stars">
                        {[0, 1, 2, 3, 4].map(s => <Star key={s} size={13} />)}
                      </span>
                      <span className="hero-trust-divider" />
                      {lang === 'ne'
                        ? '५०० भन्दा बढी भेरिफाइड नेपाली विवाह सेवा प्रदायक'
                        : 'Trusted by 500+ verified Nepali wedding vendors'}
                    </span>
                    <h1 className="hero-title">
                      {heroCopy[i % heroCopy.length].title} <span className="highlight">{heroCopy[i % heroCopy.length].highlight}</span>
                    </h1>
                    <p className="hero-subtitle">{heroCopy[i % heroCopy.length].sub}</p>

                    {/* DWT Royal 2-field search bar */}
                    <div className="search-bar-royal">
                      <div className="sbr-field sbr-select">
                        <MapPin size={15} className="sbr-icon" />
                        <select value={cityQuery} onChange={(e) => setCityQuery(e.target.value)}>
                          <option value="">{t('home.selectLocation')}</option>
                          {cities.map(city => <option key={city} value={city}>{city}</option>)}
                        </select>
                      </div>
                      <div className="sbr-divider" />
                      <div className="sbr-field sbr-select">
                        <Store size={15} className="sbr-icon" />
                        <select value={categoryQuery} onChange={(e) => setCategoryQuery(e.target.value)}>
                          <option value="">{t('home.selectCategory')}</option>
                          {categories.map(c => <option key={c.id} value={c.slug}>{c.name}</option>)}
                        </select>
                      </div>
                      <button className="sbr-btn" onClick={handleSearch}>
                        {t('common.search')}
                      </button>
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
          <div className="hero-swiper-pagination swiper-pagination" />
        </div>
        {/* DWT Royal colorful category tiles */}
        {categories.length > 0 && (
          <div className="hero-cat-tiles-row">
            <div className="hero-cat-tiles-inner">
              {sortedCategories.slice(0, 10).map(cat => {
                const CatIcon = categoryIcons[cat.slug] || Store;
                return (
                  <Link key={cat.id} to={`/vendors/${cat.slug}`} className="hero-cat-tile">
                    <div className="hero-cat-tile-box" style={{ '--tile': categoryTileColors[cat.slug] || '#f07563' }}>
                      <CatIcon size={26} />
                    </div>
                    <span className="hero-cat-tile-label">{cat.name}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </section>

      {/* ── 2. FEATURES BAR ── */}
      <div className="features-bar">
        <div className="features-bar-inner">
          {[
            { to: '/invitations',     Icon: Mail,        label: t('home.featInvitations'), tip: 'Send beautiful digital invitation cards via email & SMS, and track RSVPs in one place' },
            { to: '/guests',          Icon: Users,       label: t('home.featGuestRsvp'), tip: 'Manage your full guest list, track RSVP responses, meal preferences, and attendance counts' },
            { to: '/wedding-website', Icon: Globe,       label: t('footer.weddingWebsite'), tip: 'Create a personalised wedding website to share your story, photos, and event details with guests' },
            { to: '/budget',          Icon: Wallet,      label: t('footer.budgetTracker'), tip: 'Plan and track every wedding expense, set budgets per category, and stay on top of payments' },
            { to: '/checklist',       Icon: CheckSquare, label: t('nav.checklist'), tip: 'Step-by-step wedding planning checklist so nothing gets missed from engagement to big day' },
            { to: '/helpers',         Icon: Heart,       label: t('home.featCollaborate'), tip: 'Invite family members as helpers to collaborate on planning tasks, guest lists, and more' },
            { to: '/vendors',         Icon: Store,       label: t('home.featTopVendors'), tip: 'Discover top-rated wedding vendors across Nepal — photographers, caterers, venues and more' },
            { to: '/vendors/venues',  Icon: MapPin,      label: t('footer.browseVenues'), tip: 'Find the perfect wedding venue — from grand banquet halls to intimate garden settings' },
          ].map(item => (
            <Tippy key={item.to} content={item.tip} animation="scale" placement="top" theme="yellow-oval" arrow>
              <Link to={item.to} className="feature-sticker">
                <item.Icon size={17} className="fs-icon" />
                <span>{item.label}</span>
              </Link>
            </Tippy>
          ))}
        </div>
      </div>

      {/* ── 3. LAGAN STRIP ── */}
      {upcomingLagan.length > 0 && (() => {
        const today = new Date(); today.setHours(0,0,0,0);
        const isToday = d => d.toDateString() === today.toDateString();
        const daysUntil = d => Math.round((d - today) / 86400000);
        return (
          <div className="lagan-strip-bar">
            <div className="lagan-strip-label">
              <Heart size={14} /> {lang === 'ne' ? 'शुभ लगन २०८३' : 'Upcoming Lagan 2083'}
            </div>
            <div className="lagan-strip-wrapper">
              <button className="lagan-scroll-btn lagan-scroll-left" onClick={() => scrollLaganStrip('left')} aria-label="Scroll left">
                <ChevronLeft size={18} />
              </button>
              <div className="lagan-strip-dates" ref={laganScrollRef}>
                {upcomingLagan.map((l, i) => {
                  const diff = daysUntil(l.adDateObj);
                  const badge = isToday(l.adDateObj) ? 'TODAY' : diff === 1 ? 'TOMORROW' : diff <= 7 ? `${diff}d` : null;
                  return (
                    <div key={i} className={`lagan-strip-item${badge === 'TODAY' ? ' lagan-strip-today' : ''}`}>
                      <span className="lagan-strip-bs">{BS_MONTHS_EN[l.month]} {l.day}</span>
                      <span className="lagan-strip-ad">{l.adDate.replace(/^[A-Za-z]+,\s*/, '')}</span>
                      {badge && <span className="lagan-strip-badge">{badge}</span>}
                    </div>
                  );
                })}
              </div>
              <button className="lagan-scroll-btn lagan-scroll-right" onClick={() => scrollLaganStrip('right')} aria-label="Scroll right">
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        );
      })()}

      {/* ── 4. PLANNING TOOLS ── */}
      <section className="section planning-tools">
        <div className="container">
          <span className="section-label reveal">{t('footer.planningTools')}</span>
          <h2 className="section-title reveal">{t('home.planningToolkit')}</h2>
          <p className="section-subtitle reveal">{t('home.planningToolkitSub')}</p>
          <div className="steps-grid reveal reveal-d1">
            <Link to="/vendors" className="step-card step-card-bg" style={{ backgroundImage: 'url(https://images.pexels.com/photos/33417234/pexels-photo-33417234.jpeg?auto=compress&cs=tinysrgb&w=800&h=500&fit=crop)' }}>
              <div className="step-icon-wrap"><Search size={26} /></div>
              <h3>{t('home.findVendorsTitle')}</h3>
              <p>{t('home.findVendorsDesc')}</p>
              <span className="step-card-arrow" aria-hidden="true"><ArrowRight size={18} /></span>
            </Link>
            <Link to="/checklist" className="step-card step-card-bg" style={{ backgroundImage: 'url(https://images.pexels.com/photos/4128977/pexels-photo-4128977.jpeg?auto=compress&cs=tinysrgb&w=800&h=500&fit=crop)' }}>
              <div className="step-icon-wrap"><CheckSquare size={26} /></div>
              <h3>{t('home.weddingChecklist')}</h3>
              <p>{t('home.weddingChecklistDesc')}</p>
              <span className="step-card-arrow" aria-hidden="true"><ArrowRight size={18} /></span>
            </Link>
            <Link to="/guests" className="step-card step-card-bg" style={{ backgroundImage: 'url(https://images.pexels.com/photos/11652319/pexels-photo-11652319.jpeg?auto=compress&cs=tinysrgb&w=800&h=500&fit=crop)' }}>
              <div className="step-icon-wrap"><Users size={26} /></div>
              <h3>{t('home.guestManager')}</h3>
              <p>{t('home.guestManagerDesc')}</p>
              <span className="step-card-arrow" aria-hidden="true"><ArrowRight size={18} /></span>
            </Link>
            <Link to="/budget" className="step-card step-card-bg" style={{ backgroundImage: 'url(https://images.pexels.com/photos/35059564/pexels-photo-35059564.jpeg?auto=compress&cs=tinysrgb&w=800&h=500&fit=crop)' }}>
              <div className="step-icon-wrap"><Wallet size={26} /></div>
              <h3>{t('home.budgetTracker')}</h3>
              <p>{t('home.budgetTrackerDesc')}</p>
              <span className="step-card-arrow" aria-hidden="true"><ArrowRight size={18} /></span>
            </Link>
            <Link to="/invitations" className="step-card step-card-bg" style={{ backgroundImage: 'url(https://images.pexels.com/photos/30921929/pexels-photo-30921929.jpeg?auto=compress&cs=tinysrgb&w=800&h=500&fit=crop)' }}>
              <div className="step-icon-wrap"><Mail size={26} /></div>
              <h3>{t('home.invitationCardsTitle')}</h3>
              <p>{t('home.invitationCardsDesc')}</p>
              <span className="step-card-arrow" aria-hidden="true"><ArrowRight size={18} /></span>
            </Link>
            <Link to="/wedding-website" className="step-card step-card-bg" style={{ backgroundImage: 'url(https://images.pexels.com/photos/12543680/pexels-photo-12543680.jpeg?auto=compress&cs=tinysrgb&w=800&h=500&fit=crop)' }}>
              <div className="step-icon-wrap"><Globe size={26} /></div>
              <h3>{t('home.weddingWebsiteTitle')}</h3>
              <p>{t('home.weddingWebsiteDesc')}</p>
              <span className="step-card-arrow" aria-hidden="true"><ArrowRight size={18} /></span>
            </Link>
          </div>
          <div className="tools-lagan-card">
            <div className="tools-lagan-left">
              <span className="section-label">{lang === 'ne' ? 'शुभ मुहूर्त' : 'Auspicious Dates'}</span>
              <h2 className="section-title" style={{ marginBottom: 8 }}>{t('home.laganTitle')}</h2>
              <p className="section-subtitle" style={{ marginBottom: 0 }}>
                {lang === 'ne' ? 'शुभ विवाह मुहूर्त — नेपाली पात्रो अनुसार' : 'Browse auspicious Nepali wedding dates by BS month'}
              </p>
            </div>
            <div className="tools-lagan-calendar">
              <LaganCalendar lang={lang} compact />
            </div>
          </div>
        </div>
      </section>

      {/* ── 5. STATS — light 4-column ── */}
      {showStats && <section className="stats-light-section">
        <div className="container stats-light-inner">
          <div className="stat-light-item reveal">
            <div className="stat-light-icon-wrap"><Store size={26} /></div>
            <span className="stat-light-number">500+</span>
            <span className="stat-light-label">{t('home.statVendors')}</span>
          </div>
          <div className="stat-light-divider" />
          <div className="stat-light-item reveal reveal-d1">
            <div className="stat-light-icon-wrap"><Calendar size={26} /></div>
            <span className="stat-light-number">100+</span>
            <span className="stat-light-label">{t('home.statWeddings')}</span>
          </div>
          <div className="stat-light-divider" />
          <div className="stat-light-item reveal reveal-d2">
            <div className="stat-light-icon-wrap"><MapPin size={26} /></div>
            <span className="stat-light-number">50+</span>
            <span className="stat-light-label">{t('home.statCities')}</span>
          </div>
          <div className="stat-light-divider" />
          <div className="stat-light-item reveal reveal-d3">
            <div className="stat-light-icon-wrap"><Star size={26} /></div>
            <span className="stat-light-number">4.8★</span>
            <span className="stat-light-label">{lang === 'ne' ? 'औसत रेटिङ' : 'Average Rating'}</span>
          </div>
        </div>
      </section>}

      {/* ── 5. CATEGORIES — image-background cards ── */}
      <section className="section categories-section">
        <div className="container">
          <span className="section-label reveal">{t('home.labelCategories')}</span>
          <h2 className="section-title reveal">{t('home.topCategories')}</h2>
          <p className="section-subtitle reveal">
            {lang === 'ne' ? 'नेपालका शीर्ष विवाह सेवाहरू ब्राउज गर्नुहोस्' : 'What do you need to find?'}
          </p>
          <div className="categories-img-grid reveal reveal-d1">
            {gridCategories.map((cat, i) => {
              // vendor_count already counts every vendor in our database,
              // Google-imported ones included, so nothing is added on top.
              const totalCount  = parseInt(cat.vendor_count || 0);
              const isFeature = i === 0;
              const isWide = !isFeature && i >= gridCategories.length - wideCategoryCount;
              // A thumbnail the admin uploaded wins over the stock photo; the
              // categories page that used to show it is no longer routed.
              const bgImg = cat.thumbnail_url
                ? mediaUrl(cat.thumbnail_url)
                : isFeature ? categoryImage(cat.slug, 1200, 960) : categoryImage(cat.slug, 800, 500);
              const CatIcon = categoryIcons[cat.slug] || Store;
              const desc = categoryDescriptions[cat.slug] || '';
              return (
                <Link
                  to={`/vendors/${cat.slug}`}
                  key={cat.id}
                  className={`category-img-card${isFeature ? ' category-img-card--feature' : ''}${isWide ? ' category-img-card--wide' : ''}`}
                >
                  <div className="category-img-bg" style={{ backgroundImage: `url(${bgImg})` }} />
                  <div
                    className="category-circle-icon"
                    style={{ background: categoryTileColors[cat.slug] || '#f07563' }}
                  >
                    <CatIcon size={22} color="#fff" />
                  </div>
                  <span className="category-img-arrow" aria-hidden="true"><ArrowRight size={18} /></span>
                  <div className="category-img-overlay">
                    <h4 className="category-img-name">{cat.name}</h4>
                    {desc && <p className="category-img-desc">{desc}</p>}
                    {totalCount > 0 && (
                      <span className="category-img-count">{totalCount}+ {t('home.vendorsSuffix')}</span>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── 6. FEATURED VENDORS ── */}
      <section className="section featured-section" style={{ background: '#f8f8f8' }}>
        <div className="container">
          <div className="reveal" style={{ textAlign: 'center', marginBottom: 32 }}>
            <span className="section-label">{t('home.labelBestVendors')}</span>
            <h2 className="section-title">{t('home.featuredVendors')}</h2>
            <p className="section-subtitle" style={{ marginBottom: 20 }}>{t('home.featuredVendorsSubtitle')}</p>
            <Link to="/vendors" className="btn btn-outline">
              {t('home.viewAll')} <ArrowRight size={16} />
            </Link>
          </div>
          <Swiper
            modules={[Navigation, Pagination, Autoplay]}
            navigation
            pagination={{ clickable: true }}
            autoplay={{ delay: 4000, disableOnInteraction: false, pauseOnMouseEnter: true }}
            spaceBetween={24}
            slidesPerView={1}
            breakpoints={{ 640: { slidesPerView: 2 }, 1024: { slidesPerView: 3 } }}
            className="featured-swiper"
          >
            {loadingVendors
              ? [1, 2, 3].map((i) => (
                  <SwiperSlide key={`sk-${i}`}>
                    <div className="vendor-skeleton-card">
                      <div className="vendor-skeleton-image" />
                      <div className="vendor-skeleton-body">
                        <div className="vendor-skeleton-line" style={{ width: '70%' }} />
                        <div className="vendor-skeleton-line" style={{ width: '50%' }} />
                        <div className="vendor-skeleton-line" style={{ width: '40%' }} />
                      </div>
                    </div>
                  </SwiperSlide>
                ))
              : featuredVendors.slice(0, 10).map((vendor) => (
                  <SwiperSlide key={vendor.id}>
                    <VendorCard vendor={vendor} />
                  </SwiperSlide>
                ))
            }
          </Swiper>
        </div>
      </section>

      {/* ── 7. TESTIMONIALS — 2-column DWT Royal style ── */}
      <section className="section testimonials-section">
        <div className="container">
          <span className="section-label reveal">Real Testimonials</span>
          <h2 className="section-title reveal">{t('home.realWeddings')}</h2>
          <p className="section-subtitle reveal">{t('home.realWeddingsSub')}</p>
          <div className="testimonials-royal-grid reveal reveal-d1">
            {TESTIMONIALS.map((item, i) => (
              <div key={i} className="testimonial-royal-card">
                <span className="testimonial-quote-mark" aria-hidden="true">&#8220;</span>
                <p className="testimonial-royal-quote">{item.text}</p>
                <div className="testimonial-royal-author">
                  <img className="testimonial-royal-avatar" src={item.img} alt={item.name} loading="lazy" />
                  <div>
                    <strong className="testimonial-royal-name">{item.name}</strong>
                    <span className="testimonial-royal-role"><MapPin size={13} /> {item.role}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 10. CTA ── */}
      {!user && (
        <section className="section cta-section">
          <div className="container">
            <div className="cta-content reveal">
              <Heart size={40} className="cta-icon" />
              <h2>{t('home.planDreamWedding')}</h2>
              <p>{t('home.planDreamWeddingSubtitle')}</p>
              <div className="cta-buttons">
                <Link to="/register" className="btn btn-primary btn-lg">{t('home.getStartedFree')}</Link>
                <Link to="/vendors" className="btn btn-outline btn-lg">{t('home.exploreVendors')}</Link>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

export default Home;
